import type {
    GenerateTitleDetailResponse,
    GenerationListResponse,
    ProductDetailResponseSchema,
} from '@/common/api/client/types.gen'

export type MarketplaceTexts = {
    title: string | null
    description: string | null
}

const normalizeText = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim()
    return trimmed ? trimmed : null
}

export const extractMarketplaceTextsFromDetail = (
    detail: GenerateTitleDetailResponse | undefined
): MarketplaceTexts | null => {
    if (!detail || detail.status !== 'completed') {
        return null
    }

    const title = normalizeText(detail.validation_result?.title)
    const description = normalizeText(detail.validation_result?.description)

    if (!title && !description) {
        return null
    }

    return { title, description }
}

export const extractImportMarketplaceTexts = (product: ProductDetailResponseSchema | undefined): MarketplaceTexts => ({
    title: normalizeText(product?.name),
    description: normalizeText(product?.description),
})

type ResolveDefaultMarketplaceTextsParams = {
    listItems: GenerationListResponse[] | undefined
    detail: GenerateTitleDetailResponse | undefined
    product: ProductDetailResponseSchema | undefined
}

export const resolveDefaultMarketplaceTexts = ({
    listItems,
    detail,
    product,
}: ResolveDefaultMarketplaceTextsParams): MarketplaceTexts => {
    const importTexts = extractImportMarketplaceTexts(product)
    const firstItemId = listItems?.[0]?.id

    if (!firstItemId) {
        return importTexts
    }

    if (detail?.id !== firstItemId) {
        return importTexts
    }

    const fromGeneration = extractMarketplaceTextsFromDetail(detail)
    if (!fromGeneration) {
        return importTexts
    }

    return {
        title: fromGeneration.title ?? importTexts.title,
        description: fromGeneration.description ?? importTexts.description,
    }
}
